import { BehaviorSubject, Observable, Subject, combineLatest, of } from "rxjs";
import { catchError, map, shareReplay, startWith, switchMap } from "rxjs/operators";
import {
  ActivityHistoryPage, DecisionDetail, DecisionPage, DetectedDatesSummary, DocumentPulse,
  HomeCalendarGranularity, HomeCalendarViewMode, HomeMetric, NextBestDecision, SystemActivity
} from "../../models/home";
import { KnowledgeAttentionResponse } from "../../models/home/knowledge-attention/knowledge-attention.model";
import { RecentChangesResponse } from "../../models/home/recent-changes/recent-changes.model";
import { UxError } from "../../models/ux-error.model";
import { HomeDataService } from "../../services/home-ia/home-data.service";
import { HomeKnowledgeAttentionService } from "../../services/home-ia/home-knowledge-attention.service";
import { HomeRecentChangesService } from "../../services/home-ia/home-recent-changes.service";
import { ProblemDetailsHandler } from "../../services/home-ia/problem-details-handler.service";
import { CalendarAdapter } from "./adapters/calendar.adapter";
import { HomeLoadStateFactory } from "./home.component.load-state";
import { DecisionStateFilter, LoadState, projectDecisionStateParam } from "./home.component.state";

export class HomeDashboardStore {
  readonly destroy$ = new Subject<void>();
  readonly pulseRefresh$ = new Subject<void>();
  readonly overviewRefresh$ = new Subject<void>();
  readonly nextBestDecisionRefresh$ = new Subject<void>();
  readonly decisionListRefresh$ = new Subject<void>();
  readonly calendarDecisionRefresh$ = new Subject<void>();
  readonly decisionDetailRefresh$ = new Subject<void>();
  readonly datesSummaryRefresh$ = new Subject<void>();
  readonly activityRefresh$ = new Subject<void>();
  readonly activityHistoryRefresh$ = new Subject<void>();
  readonly metricsRefresh$ = new Subject<void>();
  readonly knowledgeAttentionRefresh$ = new Subject<void>();
  readonly recentChangesRefresh$ = new Subject<void>();
  readonly overviewPatch$ = new Subject<LoadState<DecisionPage>>();
  readonly nextBestDecisionPatch$ = new Subject<LoadState<NextBestDecision | null>>();
  readonly decisionListPatch$ = new Subject<LoadState<DecisionPage>>();
  readonly calendarDecisionPatch$ = new Subject<LoadState<DecisionPage>>();
  readonly decisionDetailPatch$ = new Subject<LoadState<DecisionDetail>>();
  readonly activeStateFilterSubject = new BehaviorSubject<DecisionStateFilter>("all");
  readonly selectedDecisionIdSubject = new BehaviorSubject<string | null>(null);
  readonly activityHistoryPageSubject = new BehaviorSubject<number>(1);
  readonly actionLoadingSubject = new BehaviorSubject<boolean>(false);
  readonly actionMessageSubject = new BehaviorSubject<string | null>(null);
  readonly actionErrorSubject = new BehaviorSubject<UxError | null>(null);
  readonly viewModeSubject = new BehaviorSubject<HomeCalendarViewMode>("current");
  readonly calendarGranularitySubject = new BehaviorSubject<HomeCalendarGranularity>("month");
  readonly calendarAnchorDateSubject = new BehaviorSubject<string>(CalendarAdapter.toDateKey(new Date()));
  readonly selectedDecisionId$ = this.selectedDecisionIdSubject.asObservable();
  readonly activeStateFilter$ = this.activeStateFilterSubject.asObservable();
  readonly viewMode$ = this.viewModeSubject.asObservable();
  readonly calendarGranularity$ = this.calendarGranularitySubject.asObservable();
  readonly calendarAnchorDate$ = this.calendarAnchorDateSubject.asObservable();
  readonly actionLoading$ = this.actionLoadingSubject.asObservable();
  readonly actionMessage$ = this.actionMessageSubject.asObservable();
  readonly actionError$ = this.actionErrorSubject.asObservable();

  latestOverviewState: LoadState<DecisionPage> | null = null;
  latestNextBestDecisionState: LoadState<NextBestDecision | null> | null = null;
  latestDecisionListState: LoadState<DecisionPage> | null = null;
  latestCalendarDecisionState: LoadState<DecisionPage> | null = null;
  latestDecisionDetailState: LoadState<DecisionDetail> | null = null;

  readonly pulseState$: Observable<LoadState<DocumentPulse>>;
  readonly overviewState$: Observable<LoadState<DecisionPage>>;
  readonly nextBestDecisionState$: Observable<LoadState<NextBestDecision | null>>;
  readonly decisionListState$: Observable<LoadState<DecisionPage>>;
  readonly calendarDecisionState$: Observable<LoadState<DecisionPage>>;
  readonly decisionDetailState$: Observable<LoadState<DecisionDetail>>;
  readonly datesSummaryState$: Observable<LoadState<DetectedDatesSummary>>;
  readonly lastActivityState$: Observable<LoadState<SystemActivity>>;
  readonly activityHistoryState$: Observable<LoadState<ActivityHistoryPage>>;
  readonly metricsState$: Observable<LoadState<readonly HomeMetric[]>>;
  readonly knowledgeAttentionState$: Observable<LoadState<KnowledgeAttentionResponse>>;
  readonly recentChangesState$: Observable<LoadState<RecentChangesResponse>>;

  constructor(
    private readonly data: HomeDataService,
    knowledge: HomeKnowledgeAttentionService,
    recentChanges: HomeRecentChangesService,
    errors: ProblemDetailsHandler
  ) {
    const loads = new HomeLoadStateFactory(errors);
    this.pulseState$ = loads.create(this.pulseRefresh$, () => data.getDocumentPulse());
    this.overviewState$ = loads.createPatched(
      loads.create(this.overviewRefresh$, () => data.getDecisions(1, 6), (page) => page.items.length === 0),
      this.overviewPatch$,
      (state) => { if (!state.loading) this.latestOverviewState = state; }
    );
    this.nextBestDecisionState$ = loads.createPatched(
      loads.create(this.nextBestDecisionRefresh$, () => data.getNextBestDecision(), (value) => !value?.decision),
      this.nextBestDecisionPatch$,
      (state) => { if (!state.loading) this.latestNextBestDecisionState = state; }
    );
    this.decisionListState$ = loads.createPatched(
      this.createFilteredDecisionPage(false, errors), this.decisionListPatch$,
      (state) => { if (!state.loading) this.latestDecisionListState = state; }
    );
    this.calendarDecisionState$ = loads.createPatched(
      this.createFilteredDecisionPage(true, errors), this.calendarDecisionPatch$,
      (state) => { if (!state.loading) this.latestCalendarDecisionState = state; }
    );
    this.decisionDetailState$ = loads.createPatched(this.createDecisionDetail(errors), this.decisionDetailPatch$,
      (state) => { if (!state.loading) this.latestDecisionDetailState = state; });
    this.datesSummaryState$ = loads.create(this.datesSummaryRefresh$, () => data.getDetectedDatesSummary(), (value) => value.totalDetected === 0);
    this.lastActivityState$ = loads.create(this.activityRefresh$, () => data.getLastActivity(), (value) => !value);
    this.activityHistoryState$ = this.createActivityHistory(errors);
    this.metricsState$ = loads.create(this.metricsRefresh$, () => data.getMetrics(), (value) => value.length === 0);
    this.knowledgeAttentionState$ = loads.create(
      this.knowledgeAttentionRefresh$, () => knowledge.getAttention(), (value) => !value.featureAvailable
    );
    this.recentChangesState$ = loads.create(
      this.recentChangesRefresh$,
      () => recentChanges.getRecentChanges({ limit: 5 }),
      (value) => !value.featureAvailable || value.items.length === 0
    );
  }

  markDecisionSlicesStale(targets: readonly string[]): void {
    const targetSet = new Set(targets);
    if (targetSet.has("overview")) this.emitStale(this.latestOverviewState, this.overviewPatch$);
    if (targetSet.has("next")) this.emitStale(this.latestNextBestDecisionState, this.nextBestDecisionPatch$);
    if (targetSet.has("list")) this.emitStale(this.latestDecisionListState, this.decisionListPatch$);
    if (targetSet.has("calendar")) this.emitStale(this.latestCalendarDecisionState, this.calendarDecisionPatch$);
    if (targetSet.has("detail")) this.emitStale(this.latestDecisionDetailState, this.decisionDetailPatch$);
  }

  private createFilteredDecisionPage(calendar: boolean, errors: ProblemDetailsHandler): Observable<LoadState<DecisionPage>> {
    const refresh$ = calendar ? this.calendarDecisionRefresh$ : this.decisionListRefresh$;
    return combineLatest([refresh$.pipe(startWith(undefined)), this.activeStateFilterSubject]).pipe(
      switchMap(([, state]) => {
        const stateParam = projectDecisionStateParam(state);
        const filters = stateParam ? { state: stateParam } : undefined;
        const request = calendar ? this.data.getAllDecisions(filters) : this.data.getDecisions(1, 10, filters);
        return request.pipe(
          map((value) => ({
            data: value, loading: false, error: null, empty: value.items.length === 0,
            stale: false, lastUpdated: Date.now()
          }) as LoadState<DecisionPage>),
          startWith({
            data: null, loading: true, error: null, empty: false, stale: false, lastUpdated: null
          } as LoadState<DecisionPage>),
          catchError((error) => errors.handle(error).pipe(map((uxError) => this.failed<DecisionPage>(uxError))))
        );
      })
    );
  }

  private createDecisionDetail(errors: ProblemDetailsHandler): Observable<LoadState<DecisionDetail>> {
    return combineLatest([this.selectedDecisionIdSubject, this.decisionDetailRefresh$.pipe(startWith(undefined))]).pipe(
      switchMap(([id]) => !id
        ? of({
          data: null, loading: false, error: null, empty: true, stale: false, lastUpdated: null
        } as LoadState<DecisionDetail>)
        : this.data.getDecisionDetail(id).pipe(
          map((value) => ({
            data: value, loading: false, error: null, empty: value.decision === null,
            stale: false, lastUpdated: Date.now()
          }) as LoadState<DecisionDetail>),
          startWith({
            data: null, loading: true, error: null, empty: false, stale: false, lastUpdated: null
          } as LoadState<DecisionDetail>),
          catchError((error) => errors.handle(error).pipe(map((uxError) => this.failed<DecisionDetail>(uxError))))
        ))
    );
  }

  private createActivityHistory(errors: ProblemDetailsHandler): Observable<LoadState<ActivityHistoryPage>> {
    return combineLatest([this.activityHistoryRefresh$.pipe(startWith(undefined)), this.activityHistoryPageSubject]).pipe(
      switchMap(([, page]) => this.data.getActivityHistory(page, 6).pipe(
        map((value) => ({
          data: value, loading: false, error: null, empty: value.items.length === 0,
          stale: false, lastUpdated: Date.now()
        }) as LoadState<ActivityHistoryPage>),
        startWith({
          data: null, loading: true, error: null, empty: false, stale: false, lastUpdated: null
        } as LoadState<ActivityHistoryPage>),
        catchError((error) => errors.handle(error).pipe(map((uxError) => this.failed<ActivityHistoryPage>(uxError))))
      )),
      shareReplay({ bufferSize: 1, refCount: true })
    );
  }

  private failed<T>(error: UxError): LoadState<T> {
    return { data: null, loading: false, error, empty: false, stale: false, lastUpdated: null };
  }

  private emitStale<T>(state: LoadState<T> | null, patch$: Subject<LoadState<T>>): void {
    if (state && !state.loading && !state.stale) patch$.next({ ...state, stale: true });
  }
}
